import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Tag, Button, Space, message, Typography, Empty, Spin } from 'antd';
import { EyeOutlined, UndoOutlined } from '@ant-design/icons';
import {
  getArchivedApplications,
  unarchiveApplication,
  getApplicationLocation,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { ArchivedApplicationListItem, ArchiveReason } from '../../types';
import ArchivedApplicationDrawer from '../../components/drawers/ArchivedApplicationDrawer';
import { useListPageState } from '../../hooks/useListPageState';
import css from './Archive.module.scss';

const { Title, Text } = Typography;

const outcomeConfig: Record<ArchiveReason, { label: string; color: string; icon?: string }> = {
  accepted_offer: { label: 'Accepted Offer', color: 'green', icon: '🎉' },
  employer_rejected: { label: 'Employer Rejected', color: 'red' },
  no_response: { label: 'No Response', color: 'default' },
  position_filled: { label: 'Position Filled', color: 'orange' },
  contact_unresponsive: { label: 'Contact Unresponsive', color: 'purple' },
  other: { label: 'Other', color: 'blue' },
};

const Archive: React.FC = () => {
  const [archives, setArchives] = useState<ArchivedApplicationListItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { selectedId, page, pageSize, setPage, openDrawer, closeDrawer, redirectToBucket } =
    useListPageState({ basePath: '/archive' });
  const navigate = useNavigate();

  const selectedArchive = useMemo<ArchivedApplicationListItem | null>(
    () => (selectedId ? archives.find(a => a.applicationId === selectedId) ?? null : null),
    [archives, selectedId]
  );
  const drawerOpen = Boolean(selectedArchive);

  const fetchArchives = async (currentPage: number, currentPageSize: number) => {
    setLoading(true);
    try {
      const response = await getArchivedApplications({
        skip: (currentPage - 1) * currentPageSize,
        limit: currentPageSize,
        sort_field: 'archived_at',
        sort_method: 'desc',
      });
      setArchives(response.archives);
      setTotalCount(response.total);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArchives(page, pageSize);
  }, [page, pageSize]);

  // Resolve deep links: if id isn't in the current list, locate it (may redirect to /applications).
  useEffect(() => {
    if (!selectedId) return;
    if (loading) return;
    if (archives.some(a => a.applicationId === selectedId)) return;

    let cancelled = false;
    (async () => {
      try {
        const loc = await getApplicationLocation(selectedId, pageSize);
        if (cancelled) return;
        if (loc.bucket === 'active') {
          redirectToBucket('/applications', selectedId, loc.page);
          return;
        }
        if (loc.page !== page) {
          setPage(loc.page, undefined, { replace: true });
        }
      } catch (error) {
        if (cancelled) return;
        message.error('Archived application not found');
        navigate('/archive', { replace: true });
      }
    })();
    return () => { cancelled = true; };
    // The pagination handles are read but not listed, on purpose. `setPage`,
    // `redirectToBucket` and `navigate` are all rebuilt whenever the URL
    // changes — useListPageState keys them on `searchParams` — and changing the
    // URL is exactly what this effect does. Listing them, or `page`/`pageSize`,
    // re-enters the effect from its own `setPage` before the refetch it
    // triggers has landed, firing a second getApplicationLocation for an id
    // that is already being located. `loading`
    // and the list are the honest triggers: they are what tells us whether the
    // id is on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, archives, loading]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleViewDetails = (record: ArchivedApplicationListItem) => {
    openDrawer(record.applicationId);
  };

  const handleUnarchive = async (record: ArchivedApplicationListItem) => {
    try {
      await unarchiveApplication(record.applicationId);
      message.success(`"${record.opportunity.title}" moved back to Applications`);
      closeDrawer();
      setArchives(prev => prev.filter(a => a.id !== record.id));
      setTotalCount(prev => prev - 1);
    } catch (error) {
      message.error(getApiErrorMessage(error));
    }
  };

  const renderOutcomeBadge = (record: ArchivedApplicationListItem) => {
    const config = outcomeConfig[record.archiveReason];
    const displayLabel = record.archiveReason === 'other' && record.archiveReasonText
      ? `Other: ${record.archiveReasonText}`
      : config.label;

    return (
      <Tag color={config.color}>
        {config.icon && `${config.icon} `}{displayLabel}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'Outcome',
      key: 'outcome',
      width: 200,
      render: (_: unknown, record: ArchivedApplicationListItem) => renderOutcomeBadge(record),
    },
    {
      title: 'Title',
      key: 'title',
      width: 420,
      render: (_: unknown, record: ArchivedApplicationListItem) => (
        // TODO(a11y): make this a real <button type="button">, the way
        // OpportunitiesTab's title cell already is. Deferred rather than done
        // here because the swap is not a one-liner: this file's `.titleLink`
        // styles an <a>, so it carries none of the UA-button reset
        // (appearance/background/border/padding/font/text-align/white-space/
        // max-width) that OpportunitiesTab.module.scss needed, and changing the
        // element without that CSS would visibly alter the desktop table —
        // which CLAUDE.md pins as the frozen reference. Until then the row's
        // "View" Button calls the same handleViewDetails and is
        // keyboard-reachable; unlike OpportunitiesTab this table keeps that
        // column at every viewport, so nothing is stranded behind a pointer.
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a onClick={() => handleViewDetails(record)} className={css.titleLink}>
          {record.opportunity.title}
        </a>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 250,
      render: (_: unknown, record: ArchivedApplicationListItem) => record.company.name,
    },
    {
      title: 'Search',
      dataIndex: ['opportunity', 'search', 'searchRole'],
      key: 'search',
      width: 200,
    },
    {
      title: 'Location',
      dataIndex: ['opportunity', 'search', 'searchLocation'],
      key: 'location',
      width: 240,
    },
    {
      title: 'Archived',
      dataIndex: 'archivedAt',
      key: 'archived',
      width: 140,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_: unknown, record: ArchivedApplicationListItem) => (
        <Space size="small">
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            View
          </Button>
          <Button
            size="small"
            type="text"
            icon={<UndoOutlined />}
            onClick={() => handleUnarchive(record)}
          >
            Unarchive
          </Button>
        </Space>
      ),
    },
  ];

  if (loading && archives.length === 0) {
    return (
      <div className={css.archive}>
        <div className={css.header}>
          <Title level={2}>Archive</Title>
        </div>
        <Spin size="large" />
      </div>
    );
  }

  if (!loading && archives.length === 0) {
    return (
      <div className={css.archive}>
        <div className={css.header}>
          <Title level={2}>Archive</Title>
          <Text type="secondary">0 archived applications</Text>
        </div>
        <div className={css.emptyContainer}>
          <Empty
            description="No archived applications"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <p>Completed or closed applications will be moved here for your records.</p>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <div className={css.archive}>
      <div className={css.header}>
        <Title level={2}>Archive</Title>
        <Text type="secondary">{totalCount} archived applications</Text>
      </div>

      <Table
        dataSource={archives}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showTotal: (total) => `${total} archived`,
          onChange: (newPage, newPageSize) => {
            setPage(newPage, newPageSize);
          },
        }}
      />

      <ArchivedApplicationDrawer
        archive={selectedArchive}
        open={drawerOpen}
        onClose={closeDrawer}
        onUnarchive={handleUnarchive}
      />
    </div>
  );
};

export default Archive;
